import { useRef, useCallback, useEffect, useMemo } from 'react';
import {
  ANCHOR_SIZES,
  ICON_SIZES,
  MARKER_SVGS,
  ZINDEXES
} from '../../../map-search-v9/lib/markers';

/**
 * Hook to handle provider highlighting on map markers
 * Works with custom marker components to provide dynamic highlighting
 *
 * @param {Array} providers - Array of provider objects
 * @param {string|null} hoveredProviderId - Currently hovered provider ID
 *
 * @returns {Object} - Highlighting functions and marker management
 */
export const useMapHighlighting = (providers, hoveredProviderId) => {
  const markersRef = useRef([]);

  const providerLocationMap = useMemo(() => {
    const map = new Map();
    if (providers && Array.isArray(providers)) {
      providers.forEach((provider) => {
        if (provider.locations) {
          provider.locations.forEach((location) => {
            map.set(location.id, provider.id);
          });
        }
      });
    }
    return map;
  }, [providers]);

  // Google Maps highlighting with custom MARKERS icons
  const highlightGoogleMapsMarkers = useCallback(
    (providerId) => {
      if (!window || !window.google) return;

      markersRef.current.forEach(({ marker, meta }) => {
        const markerProviderId =
          meta.location.providerId || providerLocationMap.get(meta.location.id);

        if (marker && marker.setIcon) {
          const isHighlighted = providerId && markerProviderId === providerId;

          // Use pre-encoded SVG data URLs
          const dataUrl = isHighlighted
            ? MARKER_SVGS.FOCUSED
            : MARKER_SVGS.UNFOCUSED;

          const iconConfig = {
            url: dataUrl,
            scaledSize: new window.google.maps.Size(
              isHighlighted
                ? ICON_SIZES.FOCUSED.width
                : ICON_SIZES.UNFOCUSED.width,
              isHighlighted
                ? ICON_SIZES.FOCUSED.height
                : ICON_SIZES.UNFOCUSED.height
            ),
            anchor: new window.google.maps.Point(
              isHighlighted
                ? ANCHOR_SIZES.FOCUSED.width
                : ANCHOR_SIZES.UNFOCUSED.width,
              isHighlighted
                ? ANCHOR_SIZES.FOCUSED.height
                : ANCHOR_SIZES.UNFOCUSED.height
            )
          };

          marker.setIcon(iconConfig);
          marker.setZIndex(
            isHighlighted ? ZINDEXES.FOCUSED : ZINDEXES.UNFOCUSED
          );
        }
      });
    },
    [providerLocationMap]
  );

  // Unified highlighting function (detects map type)
  const highlightMarkers = useCallback(
    (providerId) => {
      if (!markersRef.current.length || !window || !window.google) return;

      // Detect map type from first marker
      const firstMarker = markersRef.current[0]?.marker;
      const isGoogleMaps = firstMarker && firstMarker.setIcon;

      if (isGoogleMaps) {
        highlightGoogleMapsMarkers(providerId);
      }
    },
    [highlightGoogleMapsMarkers]
  );

  // Handle hover changes
  useEffect(() => {
    highlightMarkers(hoveredProviderId);
  }, [hoveredProviderId, highlightMarkers]);

  const setMarkers = (markers) => {
    markersRef.current = markers || [];
  };

  return {
    highlightMarkers,
    setMarkers
  };
};
